import { PageProps } from "@/types/page";
import Intercom, { shutdown } from "@intercom/messenger-js-sdk";
import axios from "axios";
import { useEffect, useRef } from "react";
import { usePage } from "./turboSafeInertia";

type IntercomJWTResponse = {
    token: string | null;
};

const TOKEN_EXPIRATION_TIME = 4 * 60 * 60 * 1000; // 4 hours

export default function useIntercomMessenger() {
    const user = usePage<PageProps>().props.user;
    const initialized = useRef(false);

    const getIntercomJWT = async (): Promise<string | null> => {
        const response = await axios.get<IntercomJWTResponse>(
            route("intercom.jwt"),
        );
        return response.data.token;
    };

    const initializeIntercom = async () => {
        if (initialized.current) {
            // If we previously initialized intercom, we need to shut it down before restarting it
            shutdown();
        }
        const token = await getIntercomJWT();
        if (token) {
            Intercom({
                app_id: import.meta.env.VITE_INTERCOM_MESSENGER_APP_ID,
                intercom_user_jwt: token,
                hide_default_launcher: true,
            });
        }
        initialized.current = true;
    };

    useEffect(() => {
        if (!user?.email_verified_at) return;
        initializeIntercom();
        const interval = setInterval(initializeIntercom, TOKEN_EXPIRATION_TIME);
        return () => {
            clearInterval(interval);
            shutdown();
        };
    }, [user?.type, user?.id, user?.email_verified_at]);
}
